import { withAlpha } from './color'

/**
 * Tokens de cor por papel. Componente usa estes nomes (`$text-primary`,
 * `$bg-surface`...), nunca a chave da paleta, para que um tema de escola troque
 * a aparência sem mexer em componente.
 *
 * No tema default cada papel aponta para a chave de paleta que a tela já usava,
 * então a aparência atual não muda.
 */
export const SEMANTIC_COLOR_KEYS = [
  'bg-page',
  'bg-chrome',
  'bg-surface',
  'bg-surface-sunken',
  'bg-surface-raised',
  'bg-surface-pressed',
  'bg-surface-strong',
  'bg-accent-soft',
  'bg-inverse',
  'bg-input',
  'bg-disabled',
  'bg-tooltip',
  'bg-overlay',
  'bg-locked',
  'skeleton-base',
  'skeleton-highlight',
  'border-subtle',
  'border-default',
  'border-strong',
  'border-muted',
  'border-hover',
  'border-focus',
  'text-primary',
  'text-body',
  'text-secondary',
  'text-muted',
  'text-placeholder',
  'text-disabled',
  'text-on-accent',
  'text-on-cta',
  'text-on-tooltip',
  'text-on-media',
  'text-on-locked',
  'icon-primary',
  'icon-secondary',
  'icon-default',
  'icon-soft',
  'icon-tertiary',
  'accent',
  'accent-hover',
  'accent-pressed',
  'accent-subtle',
  'cta',
  'cta-hover',
  'cta-pressed',
  'spinner',
  'spinner-strong',
  'scrollbar-thumb',
  'control-inactive',
  'control-active',
  'control-border',
  'control-track-off',
  'control-track-off-hover',
  'progress-track',
  'progress-fill',
  'progress-complete',
] as const

export type SemanticColorKey = (typeof SEMANTIC_COLOR_KEYS)[number]

export type SemanticColors = Record<SemanticColorKey, string>

export type PaletteColors = Record<string, string>

export function semanticFromPalette(palette: PaletteColors): SemanticColors {
  return {
    'bg-page': palette['background-100'],
    'bg-chrome': palette['neutral-0'],
    'bg-surface': palette['neutral-0'],
    'bg-surface-sunken': palette['background-100'],
    'bg-surface-raised': palette['neutral-100'],
    'bg-surface-pressed': palette['neutral-200'],
    'bg-surface-strong': palette['neutral-200'],
    'bg-accent-soft': palette['primary-100'],
    'bg-inverse': palette['neutral-800'],
    'bg-input': palette['neutral-0'],
    'bg-disabled': palette['neutral-200'],
    'bg-tooltip': palette['neutral-700'],
    'bg-overlay': withAlpha('#000000', 0.64),
    'bg-locked': palette['warning-300'],
    'skeleton-base': palette['neutral-100'],
    'skeleton-highlight': '#929292',
    'border-subtle': palette['neutral-100'],
    'border-default': palette['neutral-200'],
    'border-strong': palette['neutral-300'],
    'border-muted': palette['neutral-500'],
    'border-hover': palette['neutral-800'],
    'border-focus': palette['info-300'],
    'text-primary': palette['neutral-800'],
    'text-body': palette['neutral-700'],
    'text-secondary': palette['neutral-600'],
    'text-muted': palette['neutral-500'],
    'text-placeholder': palette['neutral-400'],
    'text-disabled': palette['neutral-500'],
    'text-on-accent': palette['neutral-0'],
    'text-on-locked': palette['neutral-0'],
    'text-on-cta': palette['cross-white'],
    'text-on-tooltip': palette['neutral-0'],
    'text-on-media': palette['cross-white'],
    'icon-primary': palette['neutral-800'],
    'icon-secondary': palette['neutral-600'],
    'icon-default': palette['neutral-200'],
    'icon-soft': palette['neutral-700'],
    'icon-tertiary': palette['neutral-300'],
    accent: palette['primary-300'],
    'accent-hover': palette['primary-200'],
    'accent-pressed': palette['primary-100'],
    'accent-subtle': palette['primary-400'],
    cta: palette['secundary-200'],
    'cta-hover': palette['secundary-300'],
    'cta-pressed': palette['secundary-400'],
    spinner: '#FF6485',
    'spinner-strong': palette['secundary-400'],
    'scrollbar-thumb': '#FF6485',
    'control-inactive': palette['neutral-300'],
    'control-active': palette['neutral-600'],
    'control-border': palette['neutral-400'],
    'control-track-off': palette['neutral-500'],
    'control-track-off-hover': palette['neutral-600'],
    'progress-track': palette['neutral-200'],
    'progress-fill': palette['info-300'],
    'progress-complete': palette['success-300'],
  }
}
